import { useState, type FormEvent } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { Button, Card, Field, SelectField } from '@pixlide/ui';
import {
  useCreateTemplate,
  type CreateTemplateStep,
  type TemplateAnimationInspection,
} from '@/api/queries/templates';
import type { components } from '@/api/generated/schema';
import {
  checkThumbnail,
  clampDuration,
  MAX_DURATION_SECONDS,
  MIN_DURATION_SECONDS,
  precheckLottie,
  TemplateFileError,
  THUMBNAIL_TYPES,
  type LottiePrecheck,
} from '@/features/templates/template-files';
import { FileDropzone } from '@/features/templates/FileDropzone';
import { errorMessage, formatLabels } from '@/features/templates/template-ui';

const createTemplateSchema = z.object({
  name: z.string().min(1, 'Le nom est obligatoire.').max(150, 'Le nom est limité à 150 caractères.'),
  description: z.string().max(2000, 'La description est limitée à 2 000 caractères.').optional(),
  category: z.string().min(1, 'La catégorie est obligatoire.').max(100, 'La catégorie est limitée à 100 caractères.'),
  format: z.coerce.number().int().min(0).max(3),
  durationSeconds: z.coerce.number().int().min(MIN_DURATION_SECONDS).max(MAX_DURATION_SECONDS),
  isPremium: z.boolean().optional(),
});

const stepLabels: Record<CreateTemplateStep, string> = {
  declaring: 'Création du brouillon…',
  uploading: 'Dépôt des fichiers…',
  analysing: 'Analyse de l’animation…',
  finishing: 'Finalisation…',
};

/**
 * Création d'un template : informations, animation Lottie (export After Effects/Bodymovin)
 * et vignette. Les fichiers sont vérifiés DANS LE NAVIGATEUR dès leur sélection — structure,
 * durée, ratio, slots — et la durée et le format sont proposés d'après l'animation elle-même.
 */
export function CreateTemplateForm({
  onCreated,
}: {
  onCreated: (inspection: TemplateAnimationInspection) => void;
}) {
  const createTemplate = useCreateTemplate();
  const [animationSource, setAnimationSource] = useState<File | null>(null);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [precheck, setPrecheck] = useState<LottiePrecheck | null>(null);
  const [animationError, setAnimationError] = useState<string | undefined>(undefined);
  const [thumbnailError, setThumbnailError] = useState<string | undefined>(undefined);
  const [step, setStep] = useState<CreateTemplateStep | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(createTemplateSchema),
    defaultValues: { format: 1, durationSeconds: 15, isPremium: false },
  });

  const selectedFormat = Number(useWatch({ control, name: 'format' })) as 0 | 1 | 2 | 3;

  const onAnimationChange = async (file: File | null) => {
    setAnimationSource(null);
    setPrecheck(null);
    setAnimationError(undefined);

    if (!file) {
      return;
    }

    try {
      const result = await precheckLottie(file);
      setAnimationSource(file);
      setPrecheck(result);

      // Valeurs PROPOSÉES d'après l'animation, toujours modifiables : la durée de sortie
      // peut volontairement différer (l'animation est alors étirée au rendu).
      setValue('durationSeconds', clampDuration(result.durationSeconds), { shouldValidate: true });

      if (result.matchingFormat !== null) {
        setValue('format', result.matchingFormat);
      }
    } catch (error) {
      setAnimationError(error instanceof TemplateFileError ? error.message : 'Fichier illisible.');
    }
  };

  const onThumbnailChange = (file: File | null) => {
    setThumbnail(null);
    setThumbnailError(undefined);

    if (!file) {
      return;
    }

    try {
      checkThumbnail(file);
      setThumbnail(file);
    } catch (error) {
      setThumbnailError(error instanceof TemplateFileError ? error.message : 'Fichier illisible.');
    }
  };

  const submitValues = handleSubmit(async (values) => {
    if (!animationSource || !thumbnail) {
      return;
    }

    try {
      const { inspection } = await createTemplate.mutateAsync({
        name: values.name,
        description: values.description || null,
        category: values.category,
        format: values.format as components['schemas']['PixlideDomainContentEnumsVideoFormat'],
        durationSeconds: values.durationSeconds,
        isPremium: values.isPremium ?? false,
        animationSource,
        thumbnail,
        onStep: setStep,
      });

      reset();
      setAnimationSource(null);
      setThumbnail(null);
      setPrecheck(null);
      onCreated(inspection);
    } catch {
      // Message rendu depuis `createTemplate.isError`. Un échec après la création du
      // brouillon le laisse dans la liste, où ses fichiers peuvent être redéposés.
    } finally {
      setStep(null);
    }
  });

  // Fichiers contrôlés AVANT la validation des champs, et non dans son rappel : celui-ci ne
  // s'exécute que si les champs sont valides, si bien qu'un fichier manquant n'était signalé
  // qu'au second essai, une fois le reste du formulaire corrigé.
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (!animationSource) {
      setAnimationError((current) => current ?? "L'animation Lottie est obligatoire.");
    }

    if (!thumbnail) {
      setThumbnailError((current) => current ?? 'La vignette est obligatoire.');
    }

    void submitValues(event);
  };

  const formatMismatch =
    precheck !== null && precheck.matchingFormat !== null && precheck.matchingFormat !== selectedFormat;

  return (
    <Card className="p-6">
      <form onSubmit={onSubmit} noValidate className="grid gap-4 sm:grid-cols-2">
        {/* Les deux fichiers en tête, côte à côte : l'animation fixe la durée et le format
            proposés plus bas, et un fichier manquant se voit avant de remplir le reste. */}
        <fieldset className="grid gap-4 sm:col-span-2 sm:grid-cols-2">
          <legend className="mb-2 text-sm font-semibold text-ink-900">Fichiers</legend>

          <FileDropzone
            label="Animation Lottie"
            hint="Export Bodymovin en JSON, 10 Mo max."
            accept="application/json,.json"
            file={animationSource}
            error={animationError}
            required
            disabled={createTemplate.isPending}
            onFile={(file) => void onAnimationChange(file)}
          >
            {precheck ? <PrecheckSummary precheck={precheck} /> : null}
          </FileDropzone>

          <FileDropzone
            label="Vignette"
            hint="JPEG, PNG, WebP ou AVIF, 5 Mo max."
            accept={THUMBNAIL_TYPES.join(',')}
            file={thumbnail}
            error={thumbnailError}
            required
            disabled={createTemplate.isPending}
            onFile={onThumbnailChange}
          />
        </fieldset>

        <Field label="Nom" error={errors.name?.message} {...register('name')} />
        <Field label="Catégorie" error={errors.category?.message} {...register('category')} />

        <div className="flex flex-col gap-1">
          <SelectField label="Format" {...register('format')}>
            {Object.entries(formatLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>
          {formatMismatch ? (
            <p className="text-xs text-warning-500">
              L’animation correspond au format {formatLabels[precheck.matchingFormat!]} : le rendu sera déformé.
            </p>
          ) : null}
        </div>

        <Field
          label="Durée (secondes)"
          type="number"
          min={MIN_DURATION_SECONDS}
          max={MAX_DURATION_SECONDS}
          error={errors.durationSeconds?.message}
          {...register('durationSeconds')}
        />

        <Field
          label="Description (optionnel)"
          error={errors.description?.message}
          className="sm:col-span-2"
          {...register('description')}
        />

        <label className="flex items-center gap-2 text-sm text-ink-700 sm:col-span-2">
          <input type="checkbox" className="size-4 rounded" {...register('isPremium')} />
          Réservé aux formules payantes
        </label>

        {createTemplate.isError ? (
          <p role="alert" className="text-xs font-medium text-danger-500 sm:col-span-2">
            {errorMessage(createTemplate.error, 'La création du template a échoué.')}
          </p>
        ) : null}

        <div className="flex items-center gap-3 sm:col-span-2">
          <Button type="submit" isLoading={isSubmitting || createTemplate.isPending}>
            Créer le template
          </Button>
          {step ? (
            <p aria-live="polite" className="text-sm text-ink-500">
              {stepLabels[step]}
            </p>
          ) : null}
        </div>
      </form>
    </Card>
  );
}

function PrecheckSummary({ precheck }: { precheck: LottiePrecheck }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg bg-ink-50 p-3 text-xs text-ink-700">
      <p>
        {precheck.width}×{precheck.height} · {precheck.durationSeconds} s à {precheck.frameRate} i/s ·{' '}
        {precheck.slotLayerCount} calque{precheck.slotLayerCount > 1 ? 's' : ''} slot-…
      </p>
      {precheck.warnings.map((warning) => (
        <p key={warning} className="text-warning-500">
          {warning}
        </p>
      ))}
    </div>
  );
}
